"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { useStylesConfigValue } from "../../stores/styles-config";
import { incrementTraceSuppression } from "../../stores/trace-suppression";
import { cn } from "../../utils";
import { ClaimDisclaimer } from "../claim-disclaimer";
import { ConversationSummary } from "../conversation-summary";
import { InsightIndicator } from "../insight-indicator";
import { PrivacyNote } from "../privacy-note";
import { RecapCard } from "../recap-card";
import { StageTransitionCard } from "../stage-transition-card";
import { StatusIndicator } from "../status-indicator";
import { StreamingIndicator } from "../streaming-indicator";
import { ThinkingIndicator } from "../thinking-indicator";
import { TraceGroup } from "../trace-group";
import { SkeletonBlock, SkeletonLine, SkeletonStack } from "../widget-loading";
import { ChatHero } from "./ChatHero";
import {
  type GroupRenderConfig,
  groupHasVisibleContent,
  isInferenceInsight,
  isItemHidden,
} from "./group-visibility";
import type {
  ChatFeedProps,
  ChatItem,
  ChatMessageGroup,
  ChatTraceGroupItem,
  ChatWidgetItem,
} from "./types";
import { useTransitionGate } from "./use-transition-gate";

/**
 * Generic fallback used when the host doesn't provide a `renderLoading`
 * dispatcher. Per-widget skeletons live next to their widgets in `sdk-react`
 * and are wired up via `ChatFeedProps.renderLoading`.
 */
function GenericWidgetLoading() {
  return (
    <div className="w-full animate-fade-in" aria-busy="true">
      <div className="rounded-3xl bg-widget-bg/40 p-4 shadow-sm">
        <SkeletonStack>
          <SkeletonLine $size="text" $width="md" />
          <SkeletonBlock $h="sm" $radius="lg" />
          <SkeletonBlock $h="sm" $radius="lg" className="w-4/5" />
        </SkeletonStack>
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );
}

/**
 * Gates rendering of the thinking/streaming loader behind a delay. Only
 * renders once `active` has been true for `delayMs` continuously — if the
 * assistant responds before the delay elapses, no loader ever appears.
 * `delayMs <= 0` renders immediately.
 */
function ThinkingLoader({
  active,
  text,
  variant,
  delayMs,
}: {
  active: boolean;
  text: string;
  variant: string;
  delayMs: number;
}) {
  const [visible, setVisible] = useState(delayMs <= 0 && active);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    if (delayMs <= 0) {
      setVisible(true);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs]);

  if (!active || !visible) return null;
  if (variant === "dots") return <StreamingIndicator active />;
  if (variant === "spinner")
    return (
      <ThinkingIndicator active cycleDuration={450}>
        {""}
      </ThinkingIndicator>
    );
  if (variant === "text-single") return <ThinkingIndicator active />;
  return <ThinkingIndicator active>{text}</ThinkingIndicator>;
}

// ---------------------------------------------------------------------------
// Per-turn render props passed down to `renderItem` / `GroupBody`. The
// `config` field is plumbed as a single object so memoized group subtrees
// observe stable references. Visibility predicates live in
// `./group-visibility`; transition-gate interaction state in
// `./use-transition-gate`.
// ---------------------------------------------------------------------------

type GroupRenderProps = {
  readonly renderText: ChatFeedProps["renderText"];
  readonly renderWidget: ChatFeedProps["renderWidget"];
  readonly renderStatus?: ChatFeedProps["renderStatus"];
  readonly renderLoading?: ChatFeedProps["renderLoading"];
  readonly onTransitionContinue?: (transitionId: string) => void;
  readonly transitionLoadingId: string | null;
  readonly dismissedTransitionIds: ReadonlySet<string>;
  readonly config: GroupRenderConfig;
};

function renderTraceGroup(item: ChatTraceGroupItem, props: GroupRenderProps) {
  const { config } = props;
  // Visibility (traceMode === "disabled", empty filtered children) is handled
  // by the `isItemHidden` fast-path in `renderItem` before we get here. What
  // remains is content transformation: in "plan" mode, inference-variant
  // insights are filtered out of the trace group's children.
  const children =
    config.traceMode === "plan"
      ? item.children.filter((c) => !isInferenceInsight(c))
      : item.children;

  return (
    <TraceGroup>
      {children.map((child) => (
        <div key={child.id} className="animate-fade-in">
          {renderItem(child, props)}
        </div>
      ))}
    </TraceGroup>
  );
}

function renderItem(item: ChatItem, props: GroupRenderProps) {
  const { config, dismissedTransitionIds } = props;

  // Single source of truth for "should this item render at all". Mirrors the
  // group-level `isItemHidden` predicate so suppression logic lives in exactly
  // one place; the group filter uses the same function.
  if (isItemHidden(item, config, dismissedTransitionIds)) {
    if (
      config.traceMode === "disabled" &&
      (item.kind === "status" || item.kind === "insight" || item.kind === "trace-group")
    ) {
      incrementTraceSuppression(item.kind);
    }
    return null;
  }

  switch (item.kind) {
    case "text":
      return props.renderText(item.text, item.role);

    case "widget":
      return props.renderWidget(item);

    case "status":
      if (props.renderStatus) return props.renderStatus(item);
      return <StatusIndicator status={item.status} context={item.context} />;

    case "insight":
      return <InsightIndicator insight={item.text} variant={item.variant} />;

    case "loading":
      if (props.renderLoading) return props.renderLoading(item);
      return <GenericWidgetLoading />;

    case "trace-group":
      return renderTraceGroup(item, props);

    case "thinking":
      return (
        <ThinkingLoader
          active={item.active}
          text={item.text}
          variant={config.loadingVariant}
          delayMs={config.loadingDelayMs}
        />
      );

    case "hero":
      return <ChatHero heading={item.heading} subheading={item.subheading} />;

    case "recap":
      return <RecapCard data={item} />;

    case "privacy-note":
      return <PrivacyNote />;

    case "transition": {
      if (!props.onTransitionContinue) return null;
      // When milestones[] is present, render the card's milestone variant
      // using the **last** newly-fired entry. The full array is preserved on
      // the chat item for telemetry/debug.
      const lastMilestone = item.milestones?.[item.milestones.length - 1];
      return (
        <StageTransitionCard
          title={lastMilestone ? lastMilestone.label : item.title}
          description={
            lastMilestone ? (lastMilestone.description ?? item.description) : item.description
          }
          variant={lastMilestone ? "milestone" : "group"}
          onContinue={() => props.onTransitionContinue?.(item.id)}
          loading={props.transitionLoadingId === item.id}
        />
      );
    }

    case "claim-disclaimer":
      return <ClaimDisclaimer disclaimer={item.disclaimer} historic={item.historic} />;

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Group renderer — renders a single ChatMessageGroup's items. The outer
// wrapping (padding, snap-start, content-visibility) is applied once at group
// creation by the parent.
// ---------------------------------------------------------------------------

type GroupBodyProps = {
  readonly group: ChatMessageGroup;
  readonly renderProps: GroupRenderProps;
};

function GroupBody({ group, renderProps }: GroupBodyProps) {
  const { config, dismissedTransitionIds } = renderProps;
  const stepHeightScreen = config.stepHeight === "screen";
  // Item-level visibility (dismissed transitions, historic claim-disclaimer
  // footers, etc.) is owned by `isItemHidden` — `renderItem` returns null for
  // any hidden item. No duplicate filters here.
  const visibleFooterItems = group.footerItems?.filter(
    (item) => !isItemHidden(item, config, dismissedTransitionIds),
  );
  const hasFooter = !!visibleFooterItems && visibleFooterItems.length > 0;
  return (
    <div className="flex flex-1 flex-col gap-4">
      {group.items.map((item) => {
        // When stepHeight === "screen", widgets should autofocus only once
        // they're in view (scroll-snap lands on them). Forward that signal
        // via input.autoFocusInView so widget hooks can react.
        const itemForRender =
          item.kind === "widget"
            ? ({
                ...item,
                input: { ...item.input, autoFocusInView: stepHeightScreen },
              } as ChatWidgetItem)
            : item;
        const renderedItem = renderItem(itemForRender, renderProps);
        if (!renderedItem) return null;

        return (
          <div
            key={item.id}
            className={cn(
              "animate-fade-in",
              item.kind === "transition" && config.stageTransitionEnabled && "mt-auto",
            )}
          >
            {renderedItem}
          </div>
        );
      })}
      {hasFooter && (
        <div className="mt-auto flex flex-col gap-2 pt-6">
          {visibleFooterItems?.map((item) => (
            <div key={item.id} className="animate-fade-in">
              {renderItem(item, renderProps)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders a single message group. The same component type is used for every
 * group — tail (streaming) and prior ones alike — so the tail's DOM is
 * preserved in place when a new group is appended and its
 * `animate-fade-in` children do not re-fire.
 *
 * Screen-reader announcements rely on the aria-live anchor at the outer
 * ChatFeed container; nothing at this level needs to own one.
 */
function GroupRenderer({ group, renderProps }: GroupBodyProps) {
  return <GroupBody group={group} renderProps={renderProps} />;
}

export function ChatFeed(feedProps: ChatFeedProps) {
  const {
    groups,
    onLoadMore,
    hasMoreHistory,
    isLoadingMore,
    historySummary,
    showFlexSpacer,
    hasContent,
  } = feedProps;

  const stepHeight = useStylesConfigValue("stepHeight");
  const scrollSnap = useStylesConfigValue("scrollSnap");
  const traceMode = useStylesConfigValue("traceMode");
  const stageTransition = useStylesConfigValue("stageTransition");
  const stageTransitionEnabled = stageTransition !== "disabled";
  const loadingVariant = useStylesConfigValue("loadingVariant");
  const loadingDelayMs = useStylesConfigValue("loadingDelayMs");

  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [lastStepElement, setLastStepElement] = useState<HTMLDivElement | null>(null);

  // Per-turn render-config object, rebuilt each render. Cheap to construct;
  // no stable-reference memoization is needed today (no memoized subtrees
  // consume it). Reused by the gate hook and plumbed into `baseRenderProps`.
  const config: GroupRenderConfig = {
    traceMode,
    stageTransition,
    loadingVariant,
    loadingDelayMs,
    stepHeight,
    scrollSnap: !!scrollSnap,
    stageTransitionEnabled,
  };

  // Transition-card interaction state. The hook owns the click set and
  // derives dismissal / pending-spinner / gate-index state from it — so
  // ChatFeed just reads the outputs and plumbs them into the render path.
  const {
    dismissedTransitionIds,
    pendingTransitionId,
    onTransitionClick,
    activeGateIdx,
    effectiveLastIdx,
  } = useTransitionGate({
    groups,
    status: feedProps.status,
    config,
    onContinue: feedProps.onTransitionContinue,
  });

  // Scroll so each new assistant response feels like a new screen. When the
  // last group is gated (awaiting user click on a transition card), we keep
  // pointing `lastStepElement` at the card's group so scrolling stays there;
  // dismissing reveals the next group as the new last step and the effect
  // re-fires.
  useEffect(() => {
    if (!hasContent) return;

    const timeoutRef = window.setTimeout(() => {
      lastStepElement?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);

    return () => clearTimeout(timeoutRef);
  }, [lastStepElement, hasContent]);

  useEffect(() => {
    if (!hasMoreHistory || !sentinelRef.current || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root: containerRef.current, threshold: 0.1 },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMoreHistory, isLoadingMore, onLoadMore]);

  const baseRenderProps: GroupRenderProps = {
    renderText: feedProps.renderText,
    renderWidget: feedProps.renderWidget,
    renderStatus: feedProps.renderStatus,
    renderLoading: feedProps.renderLoading,
    onTransitionContinue: onTransitionClick,
    transitionLoadingId: pendingTransitionId,
    dismissedTransitionIds,
    config,
  };

  return (
    // Outer container owns the live-region contract. `role="log"` + explicit
    // `aria-live="polite"` + `aria-relevant="additions"` — the explicit
    // `aria-live` is not redundant: several screen-reader/browser combos do
    // not announce implicit log live-regions reliably. This element is never
    // unmounted by the static/streaming split below, so screen readers see a
    // single stable anchor across every stream chunk.
    <div
      ref={containerRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label="Conversation"
      data-testid="chat-feed-log"
      className={cn(
        "min-h-full flex-1",
        !hasContent && "flex flex-col", // for gap space
      )}
    >
      {hasMoreHistory && (
        <div ref={sentinelRef} className="flex justify-center py-2">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-text-light text-xs">
              <div className="w-3 h-3 border-2 border-text-light/30 border-t-text-light rounded-full animate-spin" />
              Loading older messages...
            </div>
          ) : (
            <button
              type="button"
              onClick={onLoadMore}
              className="text-xs text-text-secondary hover:text-text transition-colors px-3 py-1.5 rounded-full border border-border cursor-pointer"
            >
              Load older messages
            </button>
          )}
        </div>
      )}

      {!hasMoreHistory && historySummary && <ConversationSummary summary={historySummary} />}

      {groups.map((group, groupIdx) => {
        const isEffectiveLast = groupIdx === effectiveLastIdx;
        const isPreamble = groupIdx === 0;

        // Hide every group from the active gate onward. Only the last gated
        // group in the feed is ever "active"; earlier ones are treated as
        // passed. This prevents premature side effects (modals, autofocus)
        // from any subsequent step the agent has already streamed in, and
        // keeps history replay working without needing persisted dismissal.
        if (activeGateIdx >= 0 && groupIdx >= activeGateIdx) return null;

        // Skip groups whose items all render null (e.g. a `visit` visible-status
        // item in its own group under `traceMode === "disabled"`). Without this,
        // the outer wrapper's `min-h-full` would claim a full-screen blank step.
        if (!groupHasVisibleContent(group, config, dismissedTransitionIds)) {
          return null;
        }

        // Group class is computed once per group and applied to the outer
        // wrapper; items inside a memoized StaticGroup never re-evaluate
        // this className during streaming.
        //
        // F4a: `snap-start snap-always` is pinned at group creation and
        // never toggled mid-stream. Prior behavior toggled snap-start on
        // each render, triggering a reflow on every chunk. `snap-always`
        // (`scroll-snap-stop: always`) pairs with the content-visibility
        // skip on earlier groups so prior groups don't re-layout during
        // the tail stream. `cv-auto-group` is applied ONCE per non-tail
        // group (see F3b); tail group lays out every chunk.
        const groupClassName = cn(
          "flex flex-col pb-6",
          !isPreamble && "pt-6 md:pt-10",
          (stepHeight === "screen" || isEffectiveLast) && hasContent && "min-h-full",
          scrollSnap && "snap-start snap-always",
          !isEffectiveLast && "cv-auto-group",
        );

        const setRef = isEffectiveLast
          ? (el: HTMLDivElement | null) => {
              if (el) setLastStepElement(el);
            }
          : undefined;

        return (
          <Fragment key={group.id}>
            {groupIdx === 1 && showFlexSpacer && <div className="sm:flex-1" />}
            <div className={groupClassName} ref={setRef}>
              <GroupRenderer group={group} renderProps={baseRenderProps} />
            </div>
          </Fragment>
        );
      })}
      {showFlexSpacer && groups.length <= 1 && <div className="sm:flex-1" />}
    </div>
  );
}
